import { CheckIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

import {
  formatCompactAge,
  parseSubagentTopic,
} from '@/sections/insights/aiAssistant/utils/subagentTopic.util';
import { Conversation } from '@/types';
import { cn } from '@/utils/classname';

interface SubagentRowProps {
  /** The child conversation this row represents. */
  conversation: Conversation;
  /** True when the user is currently viewing this child (renders the viewing state). */
  isViewing: boolean;
  /** Called with the child's id when a navigable row is activated. Not called in the viewing state. */
  onNavigate: (conversationId: string) => void;
  /** True when this subagent has a pending credential request ("needs input"). */
  needsInput?: boolean;
}

/**
 * One navigable actor card in the TraceDrawer "Subagents" roster. Stateless and
 * presentational — derives everything from its props during render (the React
 * Compiler handles memoization; no manual hook-based caching). Diverges from
 * the Jobs ledger row by leading with a human agent name, a status WORD + dot
 * (no chip), and a persistent drill-in chevron.
 */
export function SubagentRow({
  conversation,
  isViewing,
  onNavigate,
  needsInput = false,
}: SubagentRowProps) {
  const childId = conversation.uuid || conversation.id || ''; // mirrors TraceDrawer.tsx:383
  const { agentLabel, target } = parseSubagentTopic(
    conversation.topic,
    childId
  );
  const isLive = conversation.status === 'active'; // mirrors TraceDrawer.tsx:396

  const statusWord = isViewing ? 'Viewing' : isLive ? 'Live' : 'Idle';
  // Compact relative age from the child's last activity (no time shown when viewing).
  const timeLabel = isViewing
    ? undefined
    : formatCompactAge(conversation.updated || conversation.created);

  const statusLine = timeLabel ? `${statusWord} · ${timeLabel}` : statusWord;
  // "needs input" is surfaced with text + aria (never color alone) so a pending
  // credential request is discoverable to assistive tech (adhering-to-uiux-laws).
  const ariaLabel = `Open ${agentLabel}'s conversation — ${statusWord}${
    needsInput ? ', needs input' : ''
  }${target ? `, target ${target}` : ''}`;

  return (
    <button
      type="button"
      onClick={isViewing ? undefined : () => onNavigate(childId)}
      aria-label={isViewing ? undefined : ariaLabel}
      aria-current={isViewing ? true : undefined}
      className={cn(
        'flex w-full flex-col gap-0.5 rounded-md border px-3 py-2 text-left transition-colors',
        'focus-visible:outline-chariot-brand-primary focus-visible:outline-2 focus-visible:outline-offset-2',
        isViewing
          ? 'border-chariot-brand-primary/30 bg-chariot-brand-primary/10 text-chariot-brand-primary cursor-default'
          : 'border-chariot-stroke bg-chariot-background-primary hover:bg-chariot-background-secondary group cursor-pointer'
      )}
    >
      {/* Row 1: status dot + agent name + status word/time + drill-in glyph */}
      <div className="flex items-center gap-2">
        {isViewing ? (
          <span className="bg-chariot-brand-primary size-2 flex-shrink-0 rounded-full" />
        ) : isLive ? (
          <span className="relative flex size-2 flex-shrink-0">
            <span className="bg-chariot-brand-primary absolute inline-flex size-full rounded-full opacity-75 motion-safe:animate-ping" />
            <span className="bg-chariot-brand-primary relative inline-flex size-2 rounded-full" />
          </span>
        ) : (
          <span className="border-chariot-stroke size-2 flex-shrink-0 rounded-full border-2" />
        )}
        <span
          className="text-chariot-text-primary flex-1 truncate text-[13px] font-semibold"
          title={agentLabel}
        >
          {agentLabel}
        </span>
        {needsInput && (
          <span
            role="img"
            aria-label="needs input"
            className="bg-chariot-status-warning/15 text-chariot-status-warning inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium"
          >
            <span className="relative flex size-1.5">
              <span className="bg-chariot-status-warning absolute inline-flex size-full rounded-full opacity-75 motion-safe:animate-ping" />
              <span className="bg-chariot-status-warning relative inline-flex size-1.5 rounded-full" />
            </span>
            Needs input
          </span>
        )}
        <span className="text-chariot-text-secondary shrink-0 text-[11px] whitespace-nowrap">
          {statusLine}
        </span>
        {isViewing ? (
          <CheckIcon className="text-chariot-brand-primary size-4 flex-shrink-0" />
        ) : (
          <ChevronRightIcon className="text-chariot-text-secondary group-hover:text-chariot-text-primary size-4 flex-shrink-0 transition-colors" />
        )}
      </div>

      {/* Row 2: cleaned mono target — omitted entirely when absent (no empty line) */}
      {target && (
        <span
          className="text-chariot-text-secondary truncate font-mono text-[11px]"
          title={target}
        >
          {target}
        </span>
      )}
    </button>
  );
}

export default SubagentRow;
